import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { DocumentTextIcon, PlusIcon, ArrowDownTrayIcon } from '@heroicons/react/24/outline';
import { toast } from 'react-hot-toast';
import { getInvoicesForCustomer } from '@/services/firebase';
import { useAuth } from '@/hooks/useAuth';
import { Invoice } from '@/types';
import LoadingSpinner from '@/components/LoadingSpinner';
import PaymentModal from '@/components/PaymentModal';
import { downloadInvoicePdf } from '@/utils/invoicePdf';

export default function CustomerInvoices() {
  const { user } = useAuth();
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedInvoice, setSelectedInvoice] = useState<Invoice | null>(null);
  const [isPaymentModalOpen, setIsPaymentModalOpen] = useState(false);
  const [downloadingId, setDownloadingId] = useState<string | null>(null);

  const handleDownload = async (invoice: Invoice) => {
    setDownloadingId(invoice.id);
    try {
      await downloadInvoicePdf(invoice);
    } catch (err) {
      console.error('Error generating invoice PDF:', err);
      toast.error('Could not generate the PDF. Please try again.');
    } finally {
      setDownloadingId(null);
    }
  };

  useEffect(() => {
    if (!user) return;
    fetchInvoices();
  }, [user]);

  const handlePaymentClick = (invoice: Invoice) => {
    setSelectedInvoice(invoice);
    setIsPaymentModalOpen(true);
  };

  const handlePaymentSuccess = () => {
    // Refresh the invoices list after successful payment
    fetchInvoices();
  };

  const fetchInvoices = async () => {
    if (!user) return;
    
    try {
      setLoading(true);
      const customerInvoices = await getInvoicesForCustomer(user.id);
      setInvoices(customerInvoices);
      setError(null);
    } catch (err) {
      console.error("Error fetching customer invoices:", err);
      setError('Failed to load invoices. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return <LoadingSpinner />;
  }

  if (error) {
    return <div className="text-center text-red-500">{error}</div>;
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'paid':
        return 'bg-green-100 text-green-800';
      case 'overdue':
        return 'bg-red-100 text-red-800';
      case 'sent':
        return 'bg-yellow-100 text-yellow-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  return (
    <div className="container mx-auto px-4 py-6">
      <h1 className="text-3xl font-bold text-gray-800 mb-6">My Invoices</h1>

      {invoices.length === 0 ? (
        <div className="rounded-lg bg-white py-12 text-center shadow-md">
          <DocumentTextIcon className="mx-auto h-12 w-12 text-gray-300" />
          <h3 className="mt-3 text-base font-medium text-gray-900">No invoices yet</h3>
          <p className="mt-1 mb-5 text-gray-500">
            Invoices appear here once we&apos;ve processed a shipment for you.
          </p>
          <Link to="/portal/shipments/new" className="btn-primary inline-flex items-center">
            <PlusIcon className="h-5 w-5 mr-2" />
            Create a Shipment
          </Link>
        </div>
      ) : (
        <div className="bg-white shadow-md rounded-lg overflow-hidden">
          <ul className="divide-y divide-gray-200">
            {invoices.map((invoice) => (
              <li key={invoice.id} className="p-6 hover:bg-gray-50 transition-colors">
                <div className="flex flex-col lg:flex-row lg:justify-between lg:items-center space-y-4 lg:space-y-0">
                  <div className="flex-1">
                    <div className="flex items-center space-x-3 mb-2">
                      <p className="text-lg font-semibold text-blue-600">
                        Invoice #{invoice.invoiceNumber}
                      </p>
                      <span className={`px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(invoice.status)}`}>
                        {invoice.status.toUpperCase()}
                      </span>
                    </div>
                    <p className="text-2xl font-bold text-gray-900 mb-2">
                      {new Intl.NumberFormat('en-US', { 
                        style: 'currency', 
                        currency: invoice.currency || 'USD' 
                      }).format(invoice.total)}
                    </p>
                    <div className="text-sm text-gray-500 space-y-1">
                      <p>Due Date: {new Date(invoice.dueDate).toLocaleDateString()}</p>
                      {invoice.shipment && (
                        <p>Shipment: {invoice.shipment.trackingNumber}</p>
                      )}
                    </div>
                  </div>
                  
                  <div className="flex flex-col sm:flex-row gap-3">
                    {invoice.status !== 'paid' && (
                      <button
                        onClick={() => handlePaymentClick(invoice)}
                        className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium"
                      >
                        Pay Now
                      </button>
                    )}
                    <button
                      onClick={() => handleDownload(invoice)}
                      disabled={downloadingId === invoice.id}
                      className="inline-flex items-center justify-center gap-2 rounded-lg border border-gray-300 px-6 py-2 font-medium text-gray-700 transition-colors hover:bg-gray-50 disabled:opacity-60"
                    >
                      <ArrowDownTrayIcon className="h-5 w-5" />
                      {downloadingId === invoice.id ? 'Preparing…' : 'PDF'}
                    </button>
                    <Link
                      to={`/portal/invoices/${invoice.id}`}
                      className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors font-medium text-center"
                    >
                      View Details
                    </Link>
                  </div>
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Payment Modal */}
      <PaymentModal
        invoice={selectedInvoice}
        isOpen={isPaymentModalOpen}
        onClose={() => {
          setIsPaymentModalOpen(false);
          setSelectedInvoice(null);
        }}
        onPaymentSuccess={handlePaymentSuccess}
      />
    </div>
  );
} 